package com.kinoshazam.screens.main.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.main.DecideCircleButton
import com.kinoshazam.screens.main.MediumBannerSkeletonCarousel
import com.kinoshazam.screens.main.continueWatching.ContinueHideMode
import com.kinoshazam.screens.main.continueWatching.ContinueWatchingSection
import com.kinoshazam.screens.rootFlow.AppVersionWatermark
import com.kinoshazam.uiKit.animations.scaleAnimation
import com.kinoshazam.uiKit.components.button.OutlinedCircleIconButton
import com.kinoshazam.uiKit.components.carousel.CarouselNavButton
import com.kinoshazam.uiKit.components.cells.cellIcon.CellIconItem
import com.kinoshazam.uiKit.components.cells.cellIcon.CellIconItemState
import com.kinoshazam.uiKit.components.cells.cellItem.CellItem
import com.kinoshazam.uiKit.components.cells.cellItem.CellItemState
import com.kinoshazam.uiKit.components.genreItem.GenreItemState
import com.kinoshazam.uiKit.components.genreItem.MediumGenreItem
import com.kinoshazam.uiKit.components.selectableText.SelectableTextState
import com.kinoshazam.uiKit.components.selectableText.SelectableTextView
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.localizedGenreName
import com.kinoshazam.uiKit.components.localizedResourceKey
import com.kinoshazam.uiKit.components.toDrawableResource
import com.kinoshazam.uiKit.components.videoItem.MediumVideoItem
import com.kinoshazam.uiKit.components.videoItem.MediumWatchAllItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.extension.thenIf
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.fantastic
import kinoshazam.resources.generated.resources.films
import kinoshazam.resources.generated.resources.films_watched
import kinoshazam.resources.generated.resources.genres
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.right
import kinoshazam.resources.generated.resources.search
import kinoshazam.resources.generated.resources.tv_shows
import kinoshazam.resources.generated.resources.tv_shows_watched
import kinoshazam.resources.generated.resources.update
import kinoshazam.resources.generated.resources.watch_all
import kinoshazam.resources.generated.resources.which_watch
import kinoshazam.resources.generated.resources.watch_history
import kinoshazam.resources.generated.resources.films_soon
import kinoshazam.resources.generated.resources.soon_in_cinema
import models.Genre
import models.VideoDetails
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosState.ScreenType.NewMovies
import presentation.screens.allVideos.AllVideosState.ScreenType.NewTvShows
import presentation.screens.allVideos.AllVideosState.ScreenType.ReleaseSoon
import presentation.screens.main.MainMVIAction
import presentation.screens.main.MainState
import presentation.screens.main.MainState.MenuItem.CARTOONS
import presentation.screens.main.MainState.MenuItem.FAVORITES
import presentation.screens.main.MainState.MenuItem.FILMS
import presentation.screens.main.MainState.MenuItem.KINOSHKA
import presentation.screens.main.MainState.MenuItem.TVSHOWS

private val DesktopContentMaxWidth = 1200.dp

private sealed interface CarouselVideoItem {
    data class Video(val details: VideoDetails) : CarouselVideoItem
    data object WatchAll : CarouselVideoItem
}

@Composable
fun MediumMainWindow(
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    DesktopMainContentScreen(
        state = state,
        onAction = onAction,
    )
}

@Composable
fun DesktopMainContentScreen(
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(UiKitTheme.colors.color_3)
            .radialTopCenterGradient(
                listOf(
                    UiKitTheme.colors.color_1.copy(alpha = 0.3f),
                    UiKitTheme.colors.color_3
                )
            )
    ) {
        Column(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .widthIn(max = DesktopContentMaxWidth)
                .fillMaxWidth()
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(start = 60.dp, end = 60.dp, top = 40.dp, bottom = 90.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (state.updateAvailable) {
                    Image(
                        painter = painterResource(Res.drawable.update),
                        colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                        contentDescription = null,
                        modifier = Modifier
                            .size(44.dp)
                            .clip(CircleShape)
                            .border(1.5.dp, UiKitTheme.colors.color_1, CircleShape)
                            .padding(10.dp)
                            .thenIf(Modifier.scale(scaleAnimation()), true)
                            .clickable {
                                onAction(MainMVIAction.ProfileClicked)
                            },
                    )
                } else {
                    CellIconItem(
                        state = CellIconItemState(
                            gap = 26.dp,
                            iconRes = state.user?.avatar,
                            iconPlaceholder = painterResource(Res.drawable.fantastic),
                        ),
                        onClick = {
                            onAction(MainMVIAction.ProfileClicked)
                        }
                    )
                }
                Spacer(Modifier.size(16.dp))
                OutlinedCircleIconButton(
                    painter = painterResource(Res.drawable.search),
                    onClick = { onAction(MainMVIAction.OnSearchClicked) },
                    size = 44.dp,
                    contentDescription = "Search",
                )
                DecideCircleButton(
                    visible = state.tasteEnabled,
                    onClick = { onAction(MainMVIAction.DecideClicked) },
                    modifier = Modifier.padding(start = 12.dp),
                )
                Spacer(Modifier.weight(1f))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    state.menuItems.forEach {
                        SelectableTextView(
                            state = SelectableTextState(
                                isSelected = state.selectedMenu == it,
                                title = localizedResourceKey(it.title),
                            ),
                            onAction = {
                                onAction(MainMVIAction.OnMenuItemClicked(it))
                            }
                        )
                    }
                }
                Spacer(Modifier.weight(1f))
            }

            when (state.selectedMenu) {
                KINOSHKA -> DesktopKinoshkaHome(state = state, onAction = onAction)

                FILMS -> {
                    val watchAllTitle = stringResource(Res.string.films_watched)
                    LargeContent(
                        genres = state.genres,
                        videos = state.newMovies,
                        genreClicked = { genre, name ->
                            onAction(
                                MainMVIAction.OnAllVideoClicked(
                                    screenType = NewMovies,
                                    title = name,
                                    genre = genre.id.toString()
                                )
                            )
                        },
                        title = "${stringResource(Res.string.films)},",
                        videoClicked = { isTvShow, id ->
                            if (isTvShow) {
                                onAction(MainMVIAction.OnTvShowClicked(id))
                            } else {
                                onAction(MainMVIAction.OnDetailsClicked(id))
                            }
                        },
                        onWatchAll = {
                            onAction(
                                MainMVIAction.OnAllVideoClicked(
                                    screenType = NewMovies,
                                    title = watchAllTitle,
                                )
                            )
                        },
                    )
                }

                TVSHOWS -> {
                    val watchAllTitle = stringResource(Res.string.tv_shows_watched)
                    LargeContent(
                        genres = state.genres,
                        videos = state.newTvShows,
                        genreClicked = { genre, name ->
                            onAction(
                                MainMVIAction.OnAllVideoClicked(
                                    screenType = NewTvShows,
                                    title = name,
                                    genre = genre.id.toString()
                                )
                            )
                        },
                        title = "${stringResource(Res.string.tv_shows)},",
                        videoClicked = { isTvShow, id ->
                            if (isTvShow) {
                                onAction(MainMVIAction.OnTvShowClicked(id))
                            } else {
                                onAction(MainMVIAction.OnDetailsClicked(id))
                            }
                        },
                        onWatchAll = {
                            onAction(
                                MainMVIAction.OnAllVideoClicked(
                                    screenType = NewTvShows,
                                    title = watchAllTitle,
                                )
                            )
                        },
                    )
                }

                CARTOONS -> {
                    CartoonLargeContent(
                        state = state,
                        onAction = onAction
                    )
                }

                FAVORITES -> MediumFavoriteContent(
                    state = state,
                    onAction = onAction
                )

                else -> Unit
            }
        }

        AppVersionWatermark(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = 28.dp),
        )
    }
}

@Composable
private fun ColumnScope.DesktopKinoshkaHome(
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    Spacer(Modifier.height(24.dp))
    if (state.watchVideo) ContinueWatchingSection(
        items = state.continueWatching,
        wide = true,
        hideMode = ContinueHideMode.Close,
        loading = !state.continueWatchingReady,
        onOpen = { onAction(MainMVIAction.OpenContinue(it)) },
        onHide = { onAction(MainMVIAction.HideContinue(it)) },
    )
    val soonTitle = stringResource(Res.string.films_soon)
    val filmsTitle = stringResource(Res.string.films_watched)
    val showsTitle = stringResource(Res.string.tv_shows_watched)
    CatalogRail(
        title = "${stringResource(Res.string.films)}, ${stringResource(Res.string.soon_in_cinema)}",
        videos = state.soonMovies,
        onVideo = { _, id -> onAction(MainMVIAction.OnDetailsClicked(id)) },
        onWatchAll = {
            onAction(
                MainMVIAction.OnAllVideoClicked(
                    screenType = ReleaseSoon,
                    title = soonTitle,
                )
            )
        },
    )
    CatalogRail(
        title = "${stringResource(Res.string.films)}, ${stringResource(Res.string.which_watch)}",
        videos = state.newMovies,
        onVideo = { isTvShow, id ->
            if (isTvShow) onAction(MainMVIAction.OnTvShowClicked(id))
            else onAction(MainMVIAction.OnDetailsClicked(id))
        },
        onWatchAll = {
            onAction(
                MainMVIAction.OnAllVideoClicked(
                    screenType = NewMovies,
                    title = filmsTitle,
                )
            )
        },
    )
    CatalogRail(
        title = "${stringResource(Res.string.tv_shows)}, ${stringResource(Res.string.which_watch)}",
        videos = state.newTvShows,
        onVideo = { _, id -> onAction(MainMVIAction.OnTvShowClicked(id)) },
        onWatchAll = {
            onAction(
                MainMVIAction.OnAllVideoClicked(
                    screenType = NewTvShows,
                    title = showsTitle,
                )
            )
        },
    )
}

@Composable
    private fun CatalogRail(
    title: String,
    videos: List<VideoDetails>?,
    onVideo: (Boolean, Int) -> Unit,
    onWatchAll: () -> Unit,
) {
    Spacer(Modifier.height(28.dp))
    CellItem(
        state = CellItemState(
            title = TextLabelState(
                text = buildAnnotatedString {
                    withStyle(SpanStyle(color = UiKitTheme.colors.color_2, fontWeight = BoldMedium.fontWeight)) {
                        append(title)
                    }
                }
            )
        )
    )
    VideosCarouselWithWatchAll(
        videos = videos,
        videoClicked = onVideo,
        onWatchAll = onWatchAll,
    )
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun ColumnScope.LargeContent(
    genres: List<Genre>,
    genreClicked: (Genre, String) -> Unit,
    title: String,
    videos: List<VideoDetails>?,
    videoClicked: (Boolean, Int) -> Unit,
    onWatchAll: () -> Unit,
) {
    Spacer(modifier = Modifier.weight(1f))

    CellItem(
        state = CellItemState(
            title = TextLabelState(
                textAlign = TextAlign.Center,
                text = buildAnnotatedString {
                    withStyle(
                        style = SpanStyle(
                            color = UiKitTheme.colors.color_2,
                            fontWeight = BoldMedium.fontWeight
                        )
                    ) {
                        append(stringResource(Res.string.genres))
                    }
                }
            )
        )
    )

    LeftAlignedCarouselRowV3<Genre>(
        modifier = Modifier.wrapContentHeight(Alignment.CenterVertically),
        items = genres,
        itemWidth = 164.dp,
        itemHeight = 120.dp,
        leftContent = { enabled, onClick ->
            CarouselNavButton(
                painter = painterResource(Res.drawable.left),
                enabled = enabled,
                contentDescription = "Prev",
                onClick = onClick,
            )
        },
        rightContent = { enabled, onClick ->
            CarouselNavButton(
                painter = painterResource(Res.drawable.right),
                enabled = enabled,
                contentDescription = "Next",
                onClick = onClick,
            )
        }
    ) { index, genre, focused ->
        val name = localizedGenreName(genre.name, genre.icon)
        MediumGenreItem(
            state = GenreItemState(
                id = genre.id ?: -1,
                title = name,
                icon = painterResource(
                    genre.icon?.toDrawableResource() ?: Res.drawable.movie
                )
            ),
            onAction = {
                genreClicked(genre, name)
            }
        )
    }

    Spacer(modifier = Modifier.weight(1f))

    CellItem(
        modifier = Modifier.wrapContentHeight(),
        state = CellItemState(
            title = TextLabelState(
                textAlign = TextAlign.Center,
                text = buildAnnotatedString {
                    withStyle(
                        style = SpanStyle(
                            color = UiKitTheme.colors.color_2,
                            fontWeight = BoldMedium.fontWeight
                        )
                    ) {
                        append(title)
                    }
                    withStyle(
                        style = SpanStyle(
                            color = UiKitTheme.colors.color_2,
                        )
                    ) {
                        append(" ${stringResource(Res.string.which_watch)}")
                    }
                }
            )
        )
    )

    VideosCarouselWithWatchAll(
        videos = videos,
        videoClicked = videoClicked,
        onWatchAll = onWatchAll,
    )

    Spacer(modifier = Modifier.weight(1f))
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
internal fun VideosCarouselWithWatchAll(
    videos: List<VideoDetails>?,
    videoClicked: (Boolean, Int) -> Unit,
    onWatchAll: () -> Unit,
) {
    if (videos == null) {
        MediumBannerSkeletonCarousel()
        return
    }
    val watchAllTitle = stringResource(Res.string.watch_all)
    val items = remember(videos) {
        videos.map { CarouselVideoItem.Video(it) } + CarouselVideoItem.WatchAll
    }

    LeftAlignedCarouselRowV3(
        items = items,
        itemWidth = 164.dp,
        itemHeight = 280.dp,
        leftContent = { enabled, onClick ->
            CarouselNavButton(
                painter = painterResource(Res.drawable.left),
                enabled = enabled,
                contentDescription = "Prev",
                onClick = onClick,
            )
        },
        rightContent = { enabled, onClick ->
            CarouselNavButton(
                painter = painterResource(Res.drawable.right),
                enabled = enabled,
                contentDescription = "Next",
                onClick = onClick,
            )
        }
    ) { _, item, _ ->
        when (item) {
            is CarouselVideoItem.Video -> {
                val video = item.details
                MediumVideoItem(
                    state = VideoState(
                        id = video.id ?: -1,
                        title = video.title.orEmpty(),
                        image = video.imageUrl,
                        placeHolder = painterResource(Res.drawable.movie),
                        placeHolderColor = UiKitTheme.colors.color_5,
                        onAction = { id ->
                            videoClicked(video.isTvShow, id)
                        }
                    ),
                )
            }

            CarouselVideoItem.WatchAll -> {
                MediumWatchAllItem(
                    title = watchAllTitle,
                    onClick = onWatchAll,
                )
            }
        }
    }
}

fun Modifier.radialTopCenterGradient(
    colors: List<Color>,
    radiusFactor: Float = 1.5f
) = drawBehind {
    drawRect(
        brush = Brush.radialGradient(
            colors = colors,
            center = Offset(size.width / 2f, 0f),
            radius = size.minDimension / radiusFactor
        )
    )
}